import React from 'react';
import {
  DateField,
  TextField,
  useTranslate,
  Show,
  NumberField,
  ReferenceField,
  FunctionField,
  useRecordContext,
  useShowContext,
  usePermissions,
} from 'react-admin';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Box,
  Chip,
  Divider,
  Paper,
  Button,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CakeIcon from '@mui/icons-material/Cake';
import HomeIcon from '@mui/icons-material/Home';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import WcIcon from '@mui/icons-material/Wc';
import StraightenIcon from '@mui/icons-material/Straighten';
import TimelineIcon from '@mui/icons-material/Timeline';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import ShieldIcon from '@mui/icons-material/Shield';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { Link as RouterLink } from 'react-router-dom';

import { MeasurementList } from '../Measurements/MeasurementList';
import LineChartField from '../LineChartField/LineChartField';
import ResultsFieldChart from '../Results/ResultsFieldChart';
import { useFeaturePreferences } from '../../config/features';

const UserHeaderCard = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const initials = `${record.firstname ? record.firstname[0] : ''}${record.lastname ? record.lastname[0] : ''}`.toUpperCase();
  const isMale = record.gender === 'male' || record.gender === true;

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 1,
        backgroundColor: '#f6f3ee',
        border: '1px solid rgba(3, 37, 23, 0.2)',
        borderTop: '4px solid #1b3b2b',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        flexWrap: 'wrap',
      }}
    >
      <Avatar
        sx={{
          width: 76,
          height: 76,
          bgcolor: '#1b3b2b',
          color: '#fed269',
          border: '2px solid #c29b38',
          fontSize: '1.8rem',
          fontWeight: 700,
          fontFamily: "'EB Garamond', serif",
        }}
      >
        {initials || <PersonIcon fontSize="large" />}
      </Avatar>

      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily: "'EB Garamond', Georgia, serif",
              color: '#032517',
            }}
          >
            {record.firstname} {record.lastname}
          </Typography>
          <Chip
            icon={<WcIcon style={{ color: '#775a00' }} />}
            label={isMale ? translate('myroot.male') : translate('myroot.female')}
            size="small"
            sx={{
              bgcolor: 'rgba(194, 155, 56, 0.15)',
              color: '#775a00',
              fontWeight: 600,
              border: '1px solid rgba(194, 155, 56, 0.3)',
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            color: '#775a00',
            letterSpacing: '0.1em',
            display: 'block',
            mb: 1,
          }}
        >
          {translate('user_show.dossier_title', {
            id: record.id,
            _: `EXPEDIENTE DEL ATLETA / PACIENTE — ID #${record.id}`,
          })}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          {record.email && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#424843' }}>
              <EmailIcon fontSize="small" sx={{ color: '#1b3b2b' }} /> {record.email}
            </span>
          )}
          {record.phone && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#424843' }}>
              <PhoneIcon fontSize="small" sx={{ color: '#1b3b2b' }} /> {record.phone}
            </span>
          )}
        </Box>
      </Box>

      <Box sx={{ ml: { xs: 0, sm: 'auto' } }}>
        <Button
          variant="contained"
          component={RouterLink}
          to={`/measurement/create?user_id=${record.id}`}
          startIcon={<PostAddIcon />}
          sx={{
            backgroundColor: '#1b3b2b',
            color: '#fed269',
            fontFamily: "'EB Garamond', Georgia, serif",
            fontWeight: 700,
            fontSize: '15px',
            border: '1px solid #c29b38',
            px: 2,
            py: 0.8,
            boxShadow: '0 2px 6px rgba(27, 59, 43, 0.2)',
            '&:hover': { backgroundColor: '#032517', color: '#fff' },
          }}
        >
          {translate('user_show.new_measurement_btn', { _: 'Nueva Medición' })}
        </Button>
      </Box>
    </Paper>
  );
};

const UserShowView = () => {
  const record = useRecordContext();
  const { isFeatureEnabled } = useFeaturePreferences();
  const { permissions } = usePermissions();
  const translate = useTranslate();

  if (!record) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#5b655f' }}>
          {translate('ra.page.loading', { _: 'Cargando expediente...' })}
        </Typography>
      </Box>
    );
  }

  const isNutritionist = permissions?.role === 'nutritionist';
  const isUnauthorized =
    isNutritionist &&
    (!record.nutritionist_id || Number(record.nutritionist_id) !== Number(permissions?.nutritionistId));

  if (isUnauthorized) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, margin: '40px auto', textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '3px double #ba1a1a',
            borderRadius: '2px',
            backgroundColor: '#fffaf9',
          }}
        >
          <ShieldIcon sx={{ fontSize: 56, color: '#ba1a1a', mb: 1.5 }} />
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'EB Garamond', serif",
              fontWeight: 700,
              color: '#ba1a1a',
              mb: 1,
            }}
          >
            {translate('auth.restricted_title', { _: 'Acceso Restringido' })}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: '#424843',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            {translate('auth.restricted_message', {
              _: 'Este expediente confidencial pertenece a otro especialista. No tiene permisos para consultar o modificar esta información.',
            })}
          </Typography>
          <Button
            variant="contained"
            href="#/user"
            sx={{
              backgroundColor: '#1b3b2b',
              color: '#fcf9f4',
              fontFamily: "'EB Garamond', serif",
              fontSize: '15px',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#032517' },
            }}
          >
            {translate('auth.return_to_list', { _: 'Volver a Mis Pacientes' })}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1200, margin: '0 auto' }}>
      <UserHeaderCard />

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Personal Info Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 1, border: '1px solid rgba(3, 37, 23, 0.15)', height: '100%', backgroundColor: '#fff' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ fontFamily: "'EB Garamond', serif", fontWeight: 700, mb: 1.5, color: '#032517', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderSharedIcon sx={{ color: '#1b3b2b' }} /> {translate('user_show.personal_data', { _: 'Datos Personales' })}
                </Typography>
                <Divider sx={{ mb: 2, borderColor: 'rgba(3, 37, 23, 0.1)' }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600 }}>
                      {translate('resources.user.fields.firstname')}
                    </Typography>
                    <Box sx={{ mt: 0.5, fontWeight: 500 }}>
                      <TextField source="firstname" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600 }}>
                      {translate('resources.user.fields.lastname')}
                    </Typography>
                    <Box sx={{ mt: 0.5, fontWeight: 500 }}>
                      <TextField source="lastname" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CakeIcon fontSize="inherit" /> {translate('resources.user.fields.birthday')}
                    </Typography>
                    <Box sx={{ mt: 0.5, fontFamily: "'JetBrains Mono', monospace" }}>
                      <DateField source="birthday" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WcIcon fontSize="inherit" /> {translate('resources.user.fields.gender')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <FunctionField
                        source="gender"
                        render={(record) =>
                          record.gender
                            ? translate('myroot.male')
                            : translate('myroot.female')
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact & Professional Info Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 1, border: '1px solid rgba(3, 37, 23, 0.15)', height: '100%', backgroundColor: '#fff' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ fontFamily: "'EB Garamond', serif", fontWeight: 700, mb: 1.5, color: '#032517', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalPharmacyIcon sx={{ color: '#c29b38' }} /> {translate('user_show.contact_assignment', { _: 'Contacto & Asignación' })}
                </Typography>
                <Divider sx={{ mb: 2, borderColor: 'rgba(3, 37, 23, 0.1)' }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="inherit" /> {translate('resources.user.fields.email')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <TextField source="email" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon fontSize="inherit" /> {translate('resources.user.fields.phone')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <TextField source="phone" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HomeIcon fontSize="inherit" /> {translate('resources.user.fields.address')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <TextField source="address" />
                    </Box>
                  </Grid>
                  {isFeatureEnabled('nutritionist') && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" sx={{ color: '#727973', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalPharmacyIcon fontSize="inherit" /> {translate('resources.user.fields.nutritionist')}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <ReferenceField
                          source="nutritionist_id"
                          reference="nutritionist"
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Measurements Card - only if measurement feature enabled */}
        {isFeatureEnabled('measurement') && (
          <Paper sx={{ p: 3, mb: 3, borderRadius: 1, border: '1px solid rgba(3, 37, 23, 0.15)', backgroundColor: '#fff' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontFamily: "'EB Garamond', serif", fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: '#032517' }}>
                <StraightenIcon sx={{ color: '#1b3b2b' }} /> {translate('user_show.evaluation_history', { _: 'Historial de Evaluaciones Antropométricas' })}
              </Typography>
              <Button
                variant="outlined"
                component={RouterLink}
                to={`/measurement/create?user_id=${record.id}`}
                startIcon={<PostAddIcon />}
                size="small"
                sx={{
                  borderColor: '#1b3b2b',
                  color: '#1b3b2b',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(27, 59, 43, 0.05)', borderColor: '#032517' },
                }}
              >
                {translate('user_show.new_measurement_btn', { _: '+ Registrar Medición' })}
              </Button>
            </Box>
            <Divider sx={{ mb: 2, borderColor: 'rgba(3, 37, 23, 0.1)' }} />
            <MeasurementList resource="measurement" filter={{ user_id: record.id }} />
          </Paper>
        )}

        {/* Analytics & Progress Charts - only if results_analytics feature enabled */}
        {isFeatureEnabled('results_analytics') && (
          <>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 1, border: '1px solid rgba(3, 37, 23, 0.15)', backgroundColor: '#fff' }}>
              <Typography variant="h6" sx={{ fontFamily: "'EB Garamond', serif", fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: '#032517' }}>
                <TimelineIcon sx={{ color: '#1b3b2b' }} /> {translate('user_show.body_comp_progression', { _: 'Progresión de Composición Corporal' })}
              </Typography>
              <Divider sx={{ mb: 2, borderColor: 'rgba(3, 37, 23, 0.1)' }} />
              <LineChartField source="id" label="" />
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 1, border: '1px solid rgba(3, 37, 23, 0.15)', backgroundColor: '#fff' }}>
              <Typography variant="h6" sx={{ fontFamily: "'EB Garamond', serif", fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: '#032517' }}>
                <AnalyticsIcon sx={{ color: '#c29b38' }} /> {translate('user_show.somatotype_chart', { _: 'Gráfico de Somatotipo' })}
              </Typography>
              <Divider sx={{ mb: 2, borderColor: 'rgba(3, 37, 23, 0.1)' }} />
              <ResultsFieldChart source="id" />
            </Paper>
          </>
        )}
      </Box>
  );
};

export const UserShow = () => {
  return (
    <Show component="div">
      <UserShowView />
    </Show>
  );
};
